import { gql } from '@apollo/client';

// Single query with feature-flag directives (includeIdentifiedVia, includeConfidence)
export const DEPENDENCIES = gql`
  query dependencies($args: DependencyInput!, $includeIdentifiedVia: Boolean = false, $includeConfidence: Boolean = false) {
    dependencies(args: $args) {
      pageInfo {
        totalCount
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          submitDatetime
          dependency {
            id
            identificationIds
            cpes
            purls
            name
            type
            subtype
            operatingSystem
            operatingSystemKernelVersion
            language
            namespace
            path
            vendor
            product
            license
            package {
              type
            }
            sbomDependenciesCount
            version {
              id
            }
            identifiers {
              uri
              type
            }
            digest {
              md5
              sha1
              sha256
            }
            description
            confidence @include(if: $includeConfidence)
          }
          analytic {
            vulnerability {
              critical
              high
              medium
              low
            }
            exploit {
              threatActor
              ransomware
              botnet
              inTheWild
              knownAttacks
              inKnownExploitedVulnerabilities
              exploitCode
            }
          }
          verification {
            heuristic
            cryptographic
            package
            symIndex
            functionHashing
            userModified
          }
          identifiedVia @include(if: $includeIdentifiedVia)
          correlations {
            assetId
            assetName
            artifact
            location
            updatedAt
            risk {
              rawScore
              score
              category
            }
          }
          latestRemediation {
            author
            reason
            remediationAction
            createdAt
            operationId
            operationType
            identificationId
          }
        }
      }
    }
  }
`;

export const DEPENDENCY_KNOWN_EXPLOITS = gql`
  query dependencyKnownExploits($args: DependencyKnownExploitsInput!) {
    dependencyKnownExploits(args: $args) {
      exploits {
        url
        name
        refsource
        dateAdded
        exploitMaturity
        exploitAvailability
      }
      botnets {
        botnetName
        dateAdded
      }
      threatActors {
        name
        aliases
        url
      }
      ransomware {
        families
        references {
          url
          date
        }
      }
      knownAttacks
      knownExploitedVulnerabilities {
        action
        dateAdded
        dueDate
        name
      }
    }
  }
`;

export const GROUPED_DEPENDENCIES = gql`
  query groupedDependencies($args: GroupedDependenciesInput!) {
    groupedDependencies(args: $args) {
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
        totalCount
      }
      edges {
        cursor
        node {
          vendor
          vendors
          components
          licenses
          license
          type
          subtype
          analytic {
            vulnerability {
              critical
              high
              medium
              low
            }
            exploit {
              threatActor
              ransomware
              botnet
              inTheWild
              inKnownExploitedVulnerabilities
              inTheWild
              exploitCode
            }
          }
        }
      }
    }
  }
`;

export const PACKAGE_DEPENDENCIES = gql`
  query packageDependenciesById($args: packageDependenciesByIdInput!) {
    packageDependenciesById(args: $args) {
      dependencies {
        direct {
          component {
            id
            name
            subtype
            package {
              type
            }
            version {
              id
              isConcrete
            }
          }
          badges
        }
        indirect {
          component {
            id
            name
            subtype
            package {
              type
            }
            version {
              id
              isConcrete
            }
          }
          badges
        }
        dependents {
          component {
            id
            name
            subtype
            package {
              type
            }
            version {
              id
              isConcrete
            }
          }
          badges
        }
      }
      associatedFiles {
        component {
          id
          name
          digest {
            md5
            sha1
            sha256
            __typename
          }
          package {
            name
            type
          }
          file {
            path
          }
        }
        badges
      }
    }
  }
`;

export const QL_REMOVE_IDENTIFICATION = gql`
  mutation removeDependencies($args: IdentificationInput!) {
    asset {
      removeDependencies(args: $args) {
        err
      }
    }
  }
`;

export const QL_ADD_DEPENDENCY = gql`
  mutation addDependency($args: AddDependencyInput!) {
    asset {
      addDependency(args: $args) {
        err
      }
    }
  }
`;

export const QL_MODIFY_DEPENDENCY = gql`
  mutation modifyDependency($args: ModifyDependencyInput!) {
    asset {
      modifyDependency(args: $args) {
        err
      }
    }
  }
`;
